// FILE: src/pages/TransactionPage.jsx
//
// Visual redesign sourced from a Claude Design handoff bundle.
// Functional contract (URL state, race-guarded fetch, lock-aware mutations,
// attachment signed URLs, server API) is preserved exactly from the previous
// implementation — only the JSX shell and styling have changed. All page CSS
// is scoped under `.txnpg-root` so nothing leaks into other pages.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import API from "../api";
import { formatMoney } from "../utils/formatMoney";

// ────────────────────────────────────────────────────────────────────
// URL state contract (matches the previous implementation)
// ────────────────────────────────────────────────────────────────────
const TRANSACTION_LIST_DEFAULT_LIMIT = 50;
const TRANSACTION_LIST_ALLOWED_LIMITS = [10, 15, 25, 50, 100];

function parseTransactionListParams(searchParams) {
  const rawPage = parseInt(searchParams.get("page"), 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const rawLimit = parseInt(searchParams.get("limit"), 10);
  const limit = TRANSACTION_LIST_ALLOWED_LIMITS.includes(rawLimit)
    ? rawLimit
    : TRANSACTION_LIST_DEFAULT_LIMIT;
  return {
    page,
    limit,
    searchText: searchParams.get("search") || "",
    filterCategory: searchParams.get("category") || "",
    filterAccount: searchParams.get("accountId") || "",
    filterStartDate: searchParams.get("startDate") || "",
    filterEndDate: searchParams.get("endDate") || "",
  };
}

// ────────────────────────────────────────────────────────────────────
// Date / money helpers
// Dates render in UK-friendly DD-Mon-YYYY (e.g. 08-May-2026) — same
// format we landed on for the Ledger CSV.
// ────────────────────────────────────────────────────────────────────
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function fmtDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")}-${
    MONTHS_SHORT[d.getUTCMonth()]
  }-${d.getUTCFullYear()}`;
}

function fmtDateLong(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getUTCDate()} ${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function fmtDateTime(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(dt.getDate())}-${MONTHS_SHORT[dt.getMonth()]}-${dt.getFullYear()} · ${pad(
    dt.getHours()
  )}:${pad(dt.getMinutes())}`;
}

// ────────────────────────────────────────────────────────────────────
// Inline stroke icons (Feather-style, matches the design bundle)
// ────────────────────────────────────────────────────────────────────
function Icon({ children, size = 16, sw = 1.6, stroke = "currentColor", fill = "none", style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flex: "none", ...style }}
    >
      {children}
    </svg>
  );
}

const ICONS = {
  search: <Icon><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Icon>,
  calendar: <Icon><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></Icon>,
  chevDown: <Icon><path d="M6 9l6 6 6-6" /></Icon>,
  chevLeft: <Icon><path d="M15 6l-6 6 6 6" /></Icon>,
  chevRight: <Icon><path d="M9 6l6 6-6 6" /></Icon>,
  plus: <Icon><path d="M12 5v14M5 12h14" /></Icon>,
  more: <Icon><circle cx="5" cy="12" r="1.4" fill="currentColor" /><circle cx="12" cy="12" r="1.4" fill="currentColor" /><circle cx="19" cy="12" r="1.4" fill="currentColor" /></Icon>,
  paperclip: <Icon><path d="M21 11.5l-8.5 8.5a5.5 5.5 0 0 1-7.78-7.78L13.5 3.74a3.7 3.7 0 1 1 5.24 5.24L10 17.7a1.9 1.9 0 0 1-2.7-2.7l7.5-7.5" /></Icon>,
  arrow: <Icon><path d="M5 12h14M13 6l6 6-6 6" /></Icon>,
  x: <Icon><path d="M6 6l12 12M6 18L18 6" /></Icon>,
  check: <Icon><path d="M4 12l5 5L20 6" /></Icon>,
  warning: <Icon><path d="M12 3l10 18H2L12 3z" /><path d="M12 10v5M12 18v.01" /></Icon>,
  lock: <Icon><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Icon>,
  upload: <Icon><path d="M12 16V4M6 10l6-6 6 6M4 18v2h16v-2" /></Icon>,
  trash: <Icon><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5" /></Icon>,
  edit: <Icon><path d="M14.5 4.5l5 5L8 21H3v-5L14.5 4.5z" /></Icon>,
  refresh: <Icon><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.3L3 16M3 21v-5h5" /></Icon>,
  download: <Icon><path d="M12 4v12M6 12l6 6 6-6M4 20h16" /></Icon>,
};

// ────────────────────────────────────────────────────────────────────
// Scoped page CSS — every selector lives under .txnpg-root.
// Sourced from the Claude Design handoff (src/styles.css), trimmed
// to the bits this page uses.
// ────────────────────────────────────────────────────────────────────
const PAGE_CSS = `
.txnpg-root {
  --white: #FFFFFF;
  --blue: #1E90FF;
  --blue-700: #1677D5;
  --blue-50: #E6F2FF;
  --yellow: #FFC107;
  --yellow-50: #FFF8E1;
  --yellow-700: #B47800;
  --green: #2E7D32;
  --green-50: #E8F5E9;
  --red: #C62828;
  --red-50: #FDECEC;
  --orange: #ED6C02;
  --orange-50: #FFF1E2;
  --ink-900: #181b1f;
  --ink-800: #212121;
  --ink-600: #4b5260;
  --ink-500: #666666;
  --ink-400: #8a8f99;
  --ink-300: #b4b8c1;
  --bg: #F4F5F7;
  --surface: #FFFFFF;
  --surface-2: #FAFBFC;
  --border: #E4E7EC;
  --border-2: #EDEFF3;
  --row-hover: #F6F8FB;
  --shadow-sm: 0 1px 0 rgba(15,23,42,.04), 0 1px 2px rgba(15,23,42,.04);
  --shadow-md: 0 1px 0 rgba(15,23,42,.04), 0 6px 18px -8px rgba(15,23,42,.18), 0 2px 6px rgba(15,23,42,.06);
  --shadow-lg: 0 1px 0 rgba(15,23,42,.04), 0 24px 56px -16px rgba(15,23,42,.30), 0 12px 24px -12px rgba(15,23,42,.16);
  --radius: 8px;
  --row-h: 40px;
  --row-pad-y: 8px;
  --table-fs: 13px;

  font-family: "IBM Plex Sans", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: var(--ink-800);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
  background: var(--bg);
  min-height: 100vh;
  padding: 20px 28px 60px;
}
.txnpg-root *, .txnpg-root *::before, .txnpg-root *::after { box-sizing: border-box; }
.txnpg-root .num, .txnpg-root .amount, .txnpg-root .cell-date {
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
}

.txnpg-root .page-wrap {
  max-width: 1480px; margin: 0 auto; width: 100%;
  display: flex; flex-direction: column; gap: 16px;
}

/* ─── Page header + Action bar ─── */
.txnpg-root .page-head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; padding-top: 6px; flex-wrap: wrap;
}
.txnpg-root .page-title {
  margin: 0; font-size: 26px; font-weight: 650; letter-spacing: -0.02em; line-height: 1.15;
}
.txnpg-root .crumbs {
  font-size: 12.5px; color: var(--ink-500); margin-top: 4px;
  display: flex; gap: 8px; align-items: center;
}
.txnpg-root .crumbs .sep { color: var(--ink-300); }
.txnpg-root .action-bar { display: flex; gap: 8px; }
.txnpg-root .actbtn {
  display: inline-flex; align-items: center; gap: 8px;
  height: 36px; padding: 0 14px 0 10px;
  border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--ink-800);
  font-weight: 600; font-size: 13px; cursor: pointer;
  transition: transform .06s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease;
}
.txnpg-root .actbtn:hover { box-shadow: var(--shadow-sm); border-color: #d4d8e0; }
.txnpg-root .actbtn:active { transform: translateY(1px); }
.txnpg-root .actbtn-plus {
  width: 20px; height: 20px; border-radius: 5px;
  display: grid; place-items: center; color: var(--white);
}
.txnpg-root .actbtn-receipt .actbtn-plus { background: var(--green); }
.txnpg-root .actbtn-payment .actbtn-plus { background: var(--red); }
.txnpg-root .actbtn-transfer .actbtn-plus { background: var(--orange); }
.txnpg-root .actbtn-receipt:hover { border-color: rgba(46,125,50,0.4); }
.txnpg-root .actbtn-payment:hover { border-color: rgba(198,40,40,0.4); }
.txnpg-root .actbtn-transfer:hover { border-color: rgba(237,108,2,0.4); }

/* ─── Filter bar ─── */
.txnpg-root .filterbar {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 14px 16px; box-shadow: var(--shadow-sm);
}
.txnpg-root .filter-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1.4fr) 1fr 1.2fr 0.85fr 0.85fr auto;
  gap: 12px 14px; align-items: end;
}
.txnpg-root .field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.txnpg-root .field-lbl {
  font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--ink-500);
}
.txnpg-root .input {
  display: flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 10px;
  background: var(--surface); border: 1px solid var(--border); border-radius: 7px;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.txnpg-root .input:focus-within { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(30,144,255,.15); }
.txnpg-root .input-pre { display: inline-flex; color: var(--ink-400); }
.txnpg-root .input input {
  flex: 1; min-width: 0; height: 100%;
  border: 0; background: transparent; outline: none;
  font-size: 13px; color: var(--ink-800); font-family: inherit;
}
.txnpg-root .input input::placeholder { color: var(--ink-400); }
.txnpg-root .input input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; }
.txnpg-root .filter-actions { display: flex; gap: 6px; align-items: flex-end; }

/* ─── Buttons ─── */
.txnpg-root .btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  height: 34px; padding: 0 14px; border-radius: 7px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  border: 1px solid transparent; background: transparent; color: var(--ink-800);
  font-family: inherit;
  transition: background .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease, transform .06s ease;
  white-space: nowrap;
}
.txnpg-root .btn:active { transform: translateY(1px); }
.txnpg-root .btn-primary {
  background: var(--blue); color: var(--white);
  box-shadow: 0 1px 0 rgba(0,0,0,.04), 0 1px 2px rgba(30,144,255,.30);
}
.txnpg-root .btn-primary:hover { background: var(--blue-700); }
.txnpg-root .btn-ghost { background: transparent; color: var(--ink-600); border-color: var(--border); }
.txnpg-root .btn-ghost:hover { background: var(--surface-2); color: var(--ink-800); }
.txnpg-root .btn-danger { background: var(--red); color: var(--white); }
.txnpg-root .btn-danger:hover { background: #a51e1e; }
.txnpg-root .btn-xs { height: 26px; padding: 0 10px; font-size: 12px; }

/* ─── Result pane + Table ─── */
.txnpg-root .result-pane {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  box-shadow: var(--shadow-sm); overflow: hidden;
}
.txnpg-root .table-wrap { overflow-x: auto; }
.txnpg-root .txn-table { width: 100%; border-collapse: collapse; font-size: var(--table-fs); }
.txnpg-root .txn-table thead th {
  position: sticky; top: 0; background: var(--surface-2); z-index: 1;
  text-align: left; padding: 10px 14px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--ink-500); border-bottom: 1px solid var(--border);
}
.txnpg-root .col-date { width: 110px; }
.txnpg-root .col-type { width: 96px; }
.txnpg-root .col-amount { width: 160px; text-align: right; }
.txnpg-root .col-actions { width: 56px; }
.txnpg-root .col-category { width: 180px; }
.txnpg-root .txn-table tbody tr {
  border-bottom: 1px solid var(--border-2);
  cursor: pointer; transition: background .12s ease;
}
.txnpg-root .txn-table tbody tr:hover { background: var(--row-hover); }
.txnpg-root .txn-table tbody tr:last-child { border-bottom: 0; }
.txnpg-root .txn-table td {
  padding: var(--row-pad-y) 14px; height: var(--row-h); vertical-align: middle;
}
.txnpg-root .cell-date { color: var(--ink-600); white-space: nowrap; font-size: 12.5px; }
.txnpg-root .cell-account { line-height: 1.25; min-width: 220px; max-width: 380px; }
.txnpg-root .cell-account > span:first-child { font-weight: 500; color: var(--ink-800); }
.txnpg-root .acct-desc {
  font-size: 12px; color: var(--ink-500); margin-top: 1px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 360px;
}
.txnpg-root .acct-pair { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.txnpg-root .acct-arrow { display: inline-flex; color: var(--orange); }
.txnpg-root .cell-category { color: var(--ink-600); white-space: nowrap; }
.txnpg-root .cell-amount { text-align: right; }
.txnpg-root .amount {
  font-weight: 600; font-size: calc(var(--table-fs) + 0.5px);
  letter-spacing: -0.01em; font-variant-numeric: tabular-nums;
}
.txnpg-root .amount-receipt { color: var(--green); }
.txnpg-root .amount-payment { color: var(--red); }
.txnpg-root .amount-transfer { color: var(--orange); }

/* Type chip */
.txnpg-root .tchip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em;
  padding: 2px 8px; border-radius: 999px; height: 20px; line-height: 1;
  white-space: nowrap;
}
.txnpg-root .tchip-receipt { background: var(--green-50); color: var(--green); }
.txnpg-root .tchip-payment { background: var(--red-50); color: var(--red); }
.txnpg-root .tchip-transfer { background: var(--orange-50); color: var(--orange); }
.txnpg-root .tchip::before {
  content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor;
}

/* Row actions */
.txnpg-root .cell-actions { text-align: right; white-space: nowrap; padding-right: 8px; }
.txnpg-root .iconbtn-row {
  width: 28px; height: 28px; border-radius: 6px;
  border: 0; background: transparent; color: var(--ink-500);
  display: inline-grid; place-items: center; cursor: pointer;
  transition: background .12s ease, color .12s ease;
}
.txnpg-root .iconbtn-row:hover { background: var(--surface-2); color: var(--ink-800); }
.txnpg-root .row-more { opacity: 0; transition: opacity .12s ease, background .12s ease, color .12s ease; }
.txnpg-root .txn-table tbody tr:hover .row-more,
.txnpg-root .txn-table tbody tr:focus-within .row-more { opacity: 1; }
.txnpg-root .inline-attach { color: var(--ink-400); }
.txnpg-root .inline-attach:hover { color: var(--blue); }

/* ─── Pagination ─── */
.txnpg-root .pagination {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 10px 14px;
  border-top: 1px solid var(--border-2); background: var(--surface-2);
  flex-wrap: wrap;
}
.txnpg-root .pag-l { display: flex; align-items: center; gap: 20px; font-size: 12.5px; color: var(--ink-500); }
.txnpg-root .pag-caption strong { color: var(--ink-800); font-weight: 600; }
.txnpg-root .pag-rpp { display: flex; align-items: center; gap: 8px; }
.txnpg-root .pag-rpp select {
  height: 28px; padding: 0 8px; font-size: 12.5px; min-width: 64px;
  border: 1px solid var(--border); border-radius: 6px; background: var(--surface);
  color: var(--ink-800); cursor: pointer; font-family: inherit;
}
.txnpg-root .pag-r { display: flex; align-items: center; gap: 2px; }
.txnpg-root .pagbtn {
  min-width: 28px; height: 28px; padding: 0 7px;
  border-radius: 6px; border: 1px solid transparent; background: transparent;
  font-size: 12.5px; color: var(--ink-600); cursor: pointer; display: inline-grid; place-items: center;
  transition: background .12s ease, color .12s ease, border-color .12s ease;
  font-variant-numeric: tabular-nums; font-family: inherit;
}
.txnpg-root .pagbtn:hover:not(:disabled) { background: var(--surface); border-color: var(--border); color: var(--ink-800); }
.txnpg-root .pagbtn.is-active { background: var(--blue); color: white; border-color: var(--blue); }
.txnpg-root .pagbtn:disabled { opacity: 0.4; cursor: not-allowed; }
.txnpg-root .pag-ellipsis { color: var(--ink-400); padding: 0 4px; }

/* ─── States ─── */
.txnpg-root .state {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 64px 16px; text-align: center; color: var(--ink-500);
}
.txnpg-root .state-illu { color: var(--ink-300); }
.txnpg-root .state-title { font-size: 15px; font-weight: 600; color: var(--ink-800); }
.txnpg-root .state-text { font-size: 13px; max-width: 380px; }
.txnpg-root .spinner {
  width: 28px; height: 28px; border-radius: 50%;
  border: 2.5px solid var(--border); border-top-color: var(--blue);
  animation: txnpg-spin .8s linear infinite;
}
@keyframes txnpg-spin { to { transform: rotate(360deg); } }

/* ─── Modal head with coloured side stripe ─── */
.txnpg-modal-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #EDEFF3;
  position: relative;
  background: #FFFFFF;
}
.txnpg-modal-head::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: #EDEFF3;
}
.txnpg-modal-head-receipt::before { background: #2E7D32; }
.txnpg-modal-head-payment::before { background: #C62828; }
.txnpg-modal-head-transfer::before { background: #ED6C02; }
.txnpg-modal-head-l { flex: 1; min-width: 0; }
.txnpg-modal-title { font-size: 16px; font-weight: 650; letter-spacing: -0.01em; color: #212121; }
.txnpg-modal-sub { font-size: 12.5px; color: #666666; margin-top: 2px; }
.txnpg-modal-close {
  width: 32px; height: 32px; border-radius: 8px; border: 0; background: transparent;
  color: #666666; display: grid; place-items: center; cursor: pointer;
}
.txnpg-modal-close:hover { background: #FAFBFC; color: #212121; }

.txnpg-modal-body { padding: 18px 20px 20px; }
.txnpg-modal-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid #EDEFF3; background: #FAFBFC;
}

/* Form (used inside modals) */
.txnpg-form { display: flex; flex-direction: column; gap: 14px; }
.txnpg-form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.txnpg-field-block { display: flex; flex-direction: column; gap: 6px; }
.txnpg-field-head {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  font-size: 12px;
}
.txnpg-field-name { font-weight: 600; color: #212121; }
.txnpg-req { color: #C62828; margin-left: 2px; }
.txnpg-field-hint { color: #8a8f99; font-size: 11.5px; }
.txnpg-field-err { color: #C62828; font-size: 11.5px; font-weight: 500; }

.txnpg-modal-body .input {
  display: flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 10px;
  background: #FFFFFF; border: 1px solid #E4E7EC; border-radius: 7px;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.txnpg-modal-body .input:focus-within { border-color: #1E90FF; box-shadow: 0 0 0 3px rgba(30,144,255,.15); }
.txnpg-modal-body .input input {
  flex: 1; min-width: 0; height: 100%;
  border: 0; background: transparent; outline: none;
  font-size: 13px; color: #212121; font-family: inherit;
}
.txnpg-modal-body .input-pre { display: inline-flex; color: #8a8f99; }
.txnpg-modal-body .input-currency { font-weight: 600; color: #666666; }
.txnpg-modal-body .input-amount input { font-size: 14.5px; font-weight: 600; font-variant-numeric: tabular-nums; }
.txnpg-modal-body .ta {
  width: 100%; resize: vertical; min-height: 70px;
  padding: 8px 10px; font-size: 13px; line-height: 1.45;
  background: #FFFFFF; border: 1px solid #E4E7EC; border-radius: 7px;
  outline: none; transition: border-color .15s ease, box-shadow .15s ease;
  font-family: inherit;
}
.txnpg-modal-body .ta:focus { border-color: #1E90FF; box-shadow: 0 0 0 3px rgba(30,144,255,.15); }

.txnpg-upload {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 8px; cursor: pointer;
  border: 1.5px dashed #E4E7EC; background: #FAFBFC;
  transition: border-color .15s ease, background .15s ease;
}
.txnpg-upload:hover { border-color: #1E90FF; background: #E6F2FF; }
.txnpg-upload input { display: none; }
.txnpg-up-ic {
  width: 32px; height: 32px; border-radius: 7px;
  background: #FFFFFF; border: 1px solid #E4E7EC; color: #1E90FF;
  display: grid; place-items: center; flex: none;
}
.txnpg-up-line1 { font-size: 13px; font-weight: 600; color: #212121; }
.txnpg-up-line2 { font-size: 11.5px; color: #666666; margin-top: 1px; }

.txnpg-attach-pill {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 6px 6px 10px;
  background: #E6F2FF; color: #1677D5;
  border-radius: 6px; font-size: 12.5px;
}
.txnpg-attach-pill .x-btn {
  width: 22px; height: 22px; border: 0; background: transparent; color: #1677D5;
  display: grid; place-items: center; border-radius: 4px; cursor: pointer;
}
.txnpg-attach-pill .x-btn:hover { background: rgba(30,144,255,.15); }

.txnpg-inline-warn {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 10px 12px; background: #FFF8E1;
  border: 1px solid rgba(255,193,7,0.5);
  border-radius: 8px; font-size: 12.5px; color: #5a4500; line-height: 1.4;
}
.txnpg-inline-warn strong { color: #B47800; }

/* View modal layout */
.txnpg-view-grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
.txnpg-view-grid.with-attach { grid-template-columns: 1.05fr 1fr; }
@media (max-width: 760px) {
  .txnpg-view-grid.with-attach { grid-template-columns: 1fr; }
}
.txnpg-view-hero {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid #EDEFF3;
}
.txnpg-view-hero .tchip { align-self: flex-start; }
.txnpg-view-amount {
  font-size: 28px; font-weight: 650; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; line-height: 1.05;
}
.txnpg-view-date { font-size: 13px; color: #666666; }
.txnpg-view-dl {
  display: grid; grid-template-columns: max-content 1fr;
  gap: 10px 18px; margin: 0; font-size: 13px;
}
.txnpg-view-dl dt {
  color: #666666; font-size: 11.5px; font-weight: 600;
  letter-spacing: 0.05em; text-transform: uppercase; padding-top: 1px;
}
.txnpg-view-dl dd { margin: 0; color: #212121; }
.txnpg-view-desc { line-height: 1.5; }
.txnpg-muted { color: #8a8f99; }

.txnpg-view-attach {
  background: #FAFBFC; border: 1px solid #EDEFF3;
  border-radius: 10px; padding: 12px; display: flex; flex-direction: column; gap: 10px;
}
.txnpg-va-head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; color: #4b5260;
}
.txnpg-va-head strong { color: #212121; font-weight: 600; }
.txnpg-va-pane {
  background: white; border: 1px solid #E4E7EC; border-radius: 8px;
  min-height: 240px; padding: 16px; display: flex; align-items: center; justify-content: center;
}
.txnpg-va-loading { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #8a8f99; font-size: 12.5px; }
.txnpg-va-img { max-width: 100%; max-height: 360px; border-radius: 4px; }
.txnpg-va-empty { color: #8a8f99; font-size: 12.5px; }
.txnpg-spinner-sm {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #E4E7EC; border-top-color: #1E90FF;
  animation: txnpg-spin .8s linear infinite;
}

/* Confirm dialog */
.txnpg-confirm { padding: 20px; min-width: 380px; }
.txnpg-confirm-title { font-size: 15px; font-weight: 650; color: #212121; }
.txnpg-confirm-text { font-size: 13px; color: #4b5260; line-height: 1.45; margin-top: 6px; }
.txnpg-confirm-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 14px; }
`;

// ────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────
function TransactionPage() {
  // ─── Data ──────────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);

  // ─── Attachments ───────────────────────────────────────────────────
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [signedAttachmentUrl, setSignedAttachmentUrl] = useState("");
  const [attachmentLoading, setAttachmentLoading] = useState(false);

  // ─── Modal state ───────────────────────────────────────────────────
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    account: "",
    fromAccount: "",
    toAccount: "",
    date: "",
    category: "",
    description: "",
    amount: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [lockedHint, setLockedHint] = useState("");

  // ─── Page lifecycle ────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // ─── URL state (filters + pagination) ──────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    page,
    limit,
    searchText: urlSearchText,
    filterCategory: urlFilterCategory,
    filterAccount: urlFilterAccount,
    filterStartDate: urlFilterStartDate,
    filterEndDate: urlFilterEndDate,
  } = parseTransactionListParams(searchParams);

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ─── Filter drafts ─────────────────────────────────────────────────
  const [searchText, setSearchText] = useState(urlSearchText);
  const [filterCategory, setFilterCategory] = useState(urlFilterCategory);
  const [filterAccount, setFilterAccount] = useState(urlFilterAccount);
  const [filterStartDate, setFilterStartDate] = useState(urlFilterStartDate);
  const [filterEndDate, setFilterEndDate] = useState(urlFilterEndDate);

  useEffect(() => setSearchText(urlSearchText), [urlSearchText]);
  useEffect(() => setFilterCategory(urlFilterCategory), [urlFilterCategory]);
  useEffect(() => setFilterAccount(urlFilterAccount), [urlFilterAccount]);
  useEffect(() => setFilterStartDate(urlFilterStartDate), [urlFilterStartDate]);
  useEffect(() => setFilterEndDate(urlFilterEndDate), [urlFilterEndDate]);

  // ─── View dialog ───────────────────────────────────────────────────
  const [viewDialog, setViewDialog] = useState({ open: false, transaction: null });

  // ─── 3-dot menu ────────────────────────────────────────────────────
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTxnForMenu, setSelectedTxnForMenu] = useState(null);

  // ─── Delete confirm ────────────────────────────────────────────────
  const [confirmDel, setConfirmDel] = useState(null);

  // ─── Currency ──────────────────────────────────────────────────────
  const [currency, setCurrency] = useState("USD");
  const currencySymbol = useMemo(() => {
    switch (currency) {
      case "USD": return "$";
      case "EUR": return "€";
      case "GBP": return "£";
      case "JPY": return "¥";
      case "BDT": return "৳";
      case "INR": return "₹";
      default: return "";
    }
  }, [currency]);

  // Race-condition guard
  const requestIdRef = useRef(0);

  // ─── Data fetchers ─────────────────────────────────────────────────
  const fetchTransactions = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await API.get("/transactions", {
        params: {
          page,
          limit,
          search: urlSearchText,
          category: urlFilterCategory,
          accountId: urlFilterAccount,
          startDate: urlFilterStartDate,
          endDate: urlFilterEndDate,
        },
      });
      if (myRequestId !== requestIdRef.current) return;
      setTransactions(response.data.transactions || []);
      setTotalCount(response.data.totalTransactions || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      if (myRequestId !== requestIdRef.current) return;
      console.error("Error fetching transactions:", error);
      setSnackbar({ open: true, message: "Failed to fetch transactions.", severity: "error" });
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false);
    }
  }, [
    page,
    limit,
    urlSearchText,
    urlFilterCategory,
    urlFilterAccount,
    urlFilterStartDate,
    urlFilterEndDate,
  ]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await API.get("/accounts?limit=10000000");
      setAccounts(res.data.accounts || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setSnackbar({ open: true, message: "Failed to fetch accounts.", severity: "error" });
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await API.get("/categories?limit=1000000");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setSnackbar({ open: true, message: "Failed to fetch categories.", severity: "error" });
    }
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchAccounts(); fetchCategories(); }, [fetchAccounts, fetchCategories]);

  useEffect(() => {
    let cancelled = false;
    API.get("/users/me?limit=1000")
      .then((res) => { if (!cancelled) setCurrency(res.data.currency || "USD"); })
      .catch((error) => console.error("Error fetching user currency:", error));
    return () => { cancelled = true; };
  }, []);

  // Re-filter categories by modal type
  useEffect(() => {
    if (modalType) {
      const lowered = modalType.toLowerCase();
      setFilteredCategories(categories.filter((cat) => cat.type.toLowerCase() === lowered));
    } else {
      setFilteredCategories([]);
    }
  }, [modalType, categories]);

  // ─── URL writer ────────────────────────────────────────────────────
  const updateUrl = useCallback(
    (updates) => {
      const next = { ...parseTransactionListParams(searchParams), ...updates };
      const params = new URLSearchParams();
      if (next.filterAccount) params.set("accountId", next.filterAccount);
      if (next.filterCategory) params.set("category", next.filterCategory);
      if (next.filterStartDate) params.set("startDate", next.filterStartDate);
      if (next.filterEndDate) params.set("endDate", next.filterEndDate);
      if (next.searchText) params.set("search", next.searchText);
      if (next.page > 1) params.set("page", String(next.page));
      if (next.limit !== TRANSACTION_LIST_DEFAULT_LIMIT)
        params.set("limit", String(next.limit));
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // ─── Filter handlers ───────────────────────────────────────────────
  const handleSearch = () => {
    updateUrl({
      searchText,
      filterCategory,
      filterAccount,
      filterStartDate,
      filterEndDate,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilterCategory("");
    setFilterAccount("");
    setFilterStartDate("");
    setFilterEndDate("");
    setSearchParams(new URLSearchParams());
  };

  // ─── Modal logic ───────────────────────────────────────────────────
  const handleOpenModal = (type) => {
    setModalType(type);
    setEditMode(false);
    setShowModal(true);
    const todayStr = new Date().toISOString().split("T")[0];
    setTransactionForm({
      account: "", fromAccount: "", toAccount: "",
      date: todayStr, category: "", description: "", amount: "",
    });
    setAttachmentFile(null);
    setFormErrors({});
    setLockedHint("");
    setCurrentTransactionId(null);
  };

  const handleEdit = (txn) => {
    setMenuAnchorEl(null);
    setSelectedTxnForMenu(null);
    setViewDialog({ open: false, transaction: null });
    setModalType(txn.type);
    setEditMode(true);
    setShowModal(true);
    setCurrentTransactionId(txn._id);
    setTransactionForm({
      account: txn.account?._id || "",
      fromAccount: txn.fromAccount?._id || "",
      toAccount: txn.toAccount?._id || "",
      date: txn.date ? txn.date.split("T")[0] : "",
      category: txn.category?._id || "",
      description: txn.description || "",
      amount: txn.amount,
    });
    setAttachmentFile(null);
    setFormErrors({});
    setLockedHint("");
  };

  const handleView = (txn) => {
    setViewDialog({ open: true, transaction: txn });
    setSignedAttachmentUrl("");
    setAttachmentLoading(false);
  };

  const handleCloseView = () => {
    setViewDialog({ open: false, transaction: null });
    setSignedAttachmentUrl("");
    setAttachmentLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentTransactionId(null);
    setTransactionForm({
      account: "", fromAccount: "", toAccount: "",
      date: "", category: "", description: "", amount: "",
    });
    setAttachmentFile(null);
    setFormErrors({});
    setLockedHint("");
  };

  // ─── Save / Delete ─────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (modalType === "Transfer") {
      if (!transactionForm.fromAccount) errs.fromAccount = "From account is required";
      if (!transactionForm.toAccount) errs.toAccount = "To account is required";
      if (
        transactionForm.fromAccount &&
        transactionForm.toAccount &&
        transactionForm.fromAccount === transactionForm.toAccount
      ) {
        errs.toAccount = "From and To must be different accounts";
      }
    } else if (!transactionForm.account) {
      errs.account = "Account is required";
    }
    if (!transactionForm.date) errs.date = "Date is required";
    if (!transactionForm.category) errs.category = "Category is required";
    if (!transactionForm.amount || Number(transactionForm.amount) <= 0) {
      errs.amount = "Amount must be greater than 0";
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    setLockedHint("");
    if (!validateForm()) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("date", transactionForm.date);
      formData.append("category", transactionForm.category);
      formData.append("description", transactionForm.description);
      formData.append("amount", transactionForm.amount);
      formData.append("type", modalType);
      if (modalType === "Transfer") {
        formData.append("fromAccount", transactionForm.fromAccount);
        formData.append("toAccount", transactionForm.toAccount);
      } else {
        formData.append("account", transactionForm.account);
      }
      if (attachmentFile) formData.append("attachment", attachmentFile);

      if (editMode && currentTransactionId) {
        await API.put(`/transactions/${currentTransactionId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSnackbar({ open: true, message: `${modalType} updated successfully!`, severity: "success" });
      } else {
        await API.post("/transactions/create", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSnackbar({ open: true, message: `${modalType} added successfully!`, severity: "success" });
      }

      await fetchTransactions();
      await fetchAccounts();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving transaction:", error);
      const errorData = error.response?.data;
      let errorMessage = "Failed to save transaction. Please try again.";
      let severity = "error";
      if (errorData) {
        errorMessage = errorData.message || errorMessage;
        if (errorData.locked) {
          errorMessage = `🔒 ${errorData.message}`;
          severity = "warning";
          setLockedHint(errorData.message);
        }
      }
      setSnackbar({ open: true, message: errorMessage, severity });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (txnId) => {
    handleCloseMenu();
    setConfirmDel(null);
    try {
      await API.delete(`/transactions/${txnId}`);
      setSnackbar({ open: true, message: "Transaction deleted successfully!", severity: "success" });
      await fetchTransactions();
      await fetchAccounts();
      handleCloseView();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      const errorData = error.response?.data;
      let errorMessage = "Failed to delete transaction.";
      let severity = "error";
      if (errorData) {
        errorMessage = errorData.message || errorMessage;
        if (errorData.locked) {
          errorMessage = `🔒 ${errorData.message}`;
          severity = "warning";
        }
      }
      setSnackbar({ open: true, message: errorMessage, severity });
    }
  };

  // ─── Pagination handlers ───────────────────────────────────────────
  const handleChangePage = (newPage) => updateUrl({ page: newPage });
  const handleChangeRowsPerPage = (newLimit) =>
    updateUrl({ limit: parseInt(newLimit, 10), page: 1 });

  // ─── Snackbar ──────────────────────────────────────────────────────
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ─── 3-dot menu ────────────────────────────────────────────────────
  const handleMenuOpen = (e, txn) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setSelectedTxnForMenu(txn);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedTxnForMenu(null);
  };

  // ─── Attachment signed URL when View dialog opens ──────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchAttachmentUrl() {
      const tx = viewDialog.transaction;
      if (!tx || !tx.attachment) {
        setSignedAttachmentUrl("");
        return;
      }
      setAttachmentLoading(true);
      try {
        const response = await API.get(`/transactions/${tx._id}/attachment-url`);
        if (cancelled) return;
        setSignedAttachmentUrl(response.data.url || "");
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching signed URL:", error);
        setSignedAttachmentUrl("");
      } finally {
        if (!cancelled) setAttachmentLoading(false);
      }
    }
    fetchAttachmentUrl();
    return () => { cancelled = true; };
  }, [viewDialog.transaction]);

  // ─── Filter dropdown options ───────────────────────────────────────
  const categoryOption = useMemo(
    () => categories.find((c) => c._id === filterCategory) || null,
    [categories, filterCategory]
  );
  const accountOption = useMemo(
    () => accounts.find((a) => a._id === filterAccount) || null,
    [accounts, filterAccount]
  );

  // ─── Pagination number list ────────────────────────────────────────
  const pageNumbers = useMemo(() => {
    const out = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) out.push(i);
    } else {
      out.push(1);
      if (page > 3) out.push("…");
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) out.push(i);
      if (page < totalPages - 2) out.push("…");
      out.push(totalPages);
    }
    return out;
  }, [page, totalPages]);

  const startIdx = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const endIdx = Math.min(totalCount, page * limit);

  const typeLower = (t) => (t ? t.toLowerCase() : "");

  return (
    <>
      <style>{PAGE_CSS}</style>
      <div className="txnpg-root">
        <div className="page-wrap">
          {/* ─── Page header + action bar ─── */}
          <header className="page-head">
            <div>
              <h1 className="page-title">Transactions</h1>
              <div className="crumbs">
                <span>Cashpro</span>
                <span className="sep">·</span>
                <span>Financial records</span>
              </div>
            </div>
            <div className="action-bar">
              <button className="actbtn actbtn-receipt" onClick={() => handleOpenModal("Receipt")}>
                <span className="actbtn-plus">{React.cloneElement(ICONS.plus, { size: 14, sw: 2.2 })}</span>
                <span>Receipt</span>
              </button>
              <button className="actbtn actbtn-payment" onClick={() => handleOpenModal("Payment")}>
                <span className="actbtn-plus">{React.cloneElement(ICONS.plus, { size: 14, sw: 2.2 })}</span>
                <span>Payment</span>
              </button>
              <button className="actbtn actbtn-transfer" onClick={() => handleOpenModal("Transfer")}>
                <span className="actbtn-plus">{React.cloneElement(ICONS.plus, { size: 14, sw: 2.2 })}</span>
                <span>Transfer</span>
              </button>
            </div>
          </header>

          {/* ─── Filter bar ─── */}
          <section
            className="filterbar"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") handleSearch();
            }}
          >
            <div className="filter-grid">
              <label className="field">
                <span className="field-lbl">Description</span>
                <div className="input">
                  <span className="input-pre">{React.cloneElement(ICONS.search, { size: 14 })}</span>
                  <input
                    type="text"
                    placeholder="Search by description…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </label>

              <label className="field">
                <span className="field-lbl">Category</span>
                <Autocomplete
                  size="small"
                  options={categories}
                  getOptionLabel={(opt) => opt.name || ""}
                  value={categoryOption}
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  onChange={(_, newVal) => setFilterCategory(newVal?._id || "")}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="All categories" variant="outlined" />
                  )}
                  ListboxProps={{ style: { maxHeight: 240 } }}
                />
              </label>

              <label className="field">
                <span className="field-lbl">Account</span>
                <Autocomplete
                  size="small"
                  options={accounts}
                  getOptionLabel={(opt) => opt.accountName || ""}
                  value={accountOption}
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  onChange={(_, newVal) => setFilterAccount(newVal?._id || "")}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="All accounts" variant="outlined" />
                  )}
                  ListboxProps={{ style: { maxHeight: 240 } }}
                />
              </label>

              <label className="field">
                <span className="field-lbl">Start date</span>
                <div className="input">
                  <span className="input-pre">{React.cloneElement(ICONS.calendar, { size: 14 })}</span>
                  <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                  />
                </div>
              </label>

              <label className="field">
                <span className="field-lbl">End date</span>
                <div className="input">
                  <span className="input-pre">{React.cloneElement(ICONS.calendar, { size: 14 })}</span>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                  />
                </div>
              </label>

              <div className="filter-actions">
                <button className="btn btn-primary" onClick={handleSearch}>
                  {React.cloneElement(ICONS.search, { size: 14 })}
                  <span>Search</span>
                </button>
                {(searchText || filterCategory || filterAccount || filterStartDate || filterEndDate) && (
                  <button className="btn btn-ghost" onClick={handleClearFilters}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ─── Result pane ─── */}
          <section className="result-pane">
            {loading ? (
              <div className="state">
                <div className="spinner" aria-hidden="true" />
                <div className="state-text">Loading transactions…</div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="state">
                <div className="state-illu" aria-hidden="true">
                  <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="10" y="14" width="44" height="36" rx="3" />
                    <path d="M10 24h44M18 32h28M18 38h22M18 44h16" />
                  </svg>
                </div>
                <div className="state-title">No transactions found</div>
                <div className="state-text">Try widening your date range or clearing some filters.</div>
                {(searchText || filterCategory || filterAccount || filterStartDate || filterEndDate) && (
                  <button className="btn btn-ghost" onClick={handleClearFilters}>Clear all filters</button>
                )}
              </div>
            ) : (
              <>
                <div className="table-wrap" role="region" aria-label="Transactions">
                  <table className="txn-table">
                    <thead>
                      <tr>
                        <th className="col-date">Date</th>
                        <th className="col-type">Type</th>
                        <th>Account</th>
                        <th className="col-category">Category</th>
                        <th className="col-amount">Amount</th>
                        <th className="col-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => {
                        const t = typeLower(txn.type);
                        return (
                          <tr key={txn._id} className={`txn-row txn-row-${t}`} onClick={() => handleView(txn)} tabIndex={0}>
                            <td className="cell-date">{fmtDate(txn.date)}</td>
                            <td>
                              <span className={`tchip tchip-${t}`}>{txn.type}</span>
                            </td>
                            <td className="cell-account">
                              {txn.type === "Transfer" ? (
                                <div className="acct-pair">
                                  <span>{txn.fromAccount?.accountName || "N/A"}</span>
                                  <span className="acct-arrow">
                                    {React.cloneElement(ICONS.arrow, { size: 12 })}
                                  </span>
                                  <span>{txn.toAccount?.accountName || "N/A"}</span>
                                </div>
                              ) : (
                                <span>{txn.account?.accountName || "N/A"}</span>
                              )}
                              {txn.description && <div className="acct-desc">{txn.description}</div>}
                            </td>
                            <td className="cell-category">{txn.category?.name || "—"}</td>
                            <td className="cell-amount">
                              <span className={`amount amount-${t}`}>
                                {t === "receipt" ? "+ " : t === "payment" ? "− " : ""}
                                {formatMoney(Number(txn.amount), currency)}
                              </span>
                            </td>
                            <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                              {txn.attachment && (
                                <button
                                  className="iconbtn-row inline-attach"
                                  onClick={(e) => { e.stopPropagation(); handleView(txn); }}
                                  aria-label="Preview attachment"
                                  title="Preview attachment"
                                >
                                  {React.cloneElement(ICONS.paperclip, { size: 14 })}
                                </button>
                              )}
                              <button
                                className="iconbtn-row row-more"
                                onClick={(e) => handleMenuOpen(e, txn)}
                                aria-label="More actions"
                              >
                                {React.cloneElement(ICONS.more, { size: 16 })}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ─── Pagination ─── */}
                <div className="pagination">
                  <div className="pag-l">
                    <span className="pag-caption">
                      Showing <strong>{startIdx}–{endIdx}</strong> of <strong>{totalCount}</strong> records
                    </span>
                    <div className="pag-rpp">
                      <span>Rows per page</span>
                      <select
                        value={limit}
                        onChange={(e) => handleChangeRowsPerPage(e.target.value)}
                      >
                        {TRANSACTION_LIST_ALLOWED_LIMITS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="pag-r">
                    <button
                      className="pagbtn"
                      disabled={page <= 1}
                      onClick={() => handleChangePage(page - 1)}
                      aria-label="Previous"
                    >
                      {React.cloneElement(ICONS.chevLeft, { size: 14 })}
                    </button>
                    {pageNumbers.map((p, i) =>
                      p === "…" ? (
                        <span key={"e" + i} className="pag-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={`pagbtn ${p === page ? "is-active" : ""}`}
                          onClick={() => handleChangePage(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      className="pagbtn"
                      disabled={page >= totalPages}
                      onClick={() => handleChangePage(page + 1)}
                      aria-label="Next"
                    >
                      {React.cloneElement(ICONS.chevRight, { size: 14 })}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        {/* ─── 3-dot context menu ─── */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => selectedTxnForMenu && handleEdit(selectedTxnForMenu)}>
            {React.cloneElement(ICONS.edit, { size: 14, style: { marginRight: 10 } })}
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedTxnForMenu) {
                setConfirmDel(selectedTxnForMenu);
                handleCloseMenu();
              }
            }}
            style={{ color: "#C62828" }}
          >
            {React.cloneElement(ICONS.trash, { size: 14, style: { marginRight: 10 } })}
            Delete
          </MenuItem>
        </Menu>

        {/* ─── Add / Edit modal ─── */}
        <Dialog
          open={showModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth={modalType === "Transfer" ? "md" : "sm"}
        >
          <div className={`txnpg-modal-head txnpg-modal-head-${typeLower(modalType)}`}>
            <div className="txnpg-modal-head-l">
              <div className="txnpg-modal-title">
                {editMode ? "Edit" : "Add"} {modalType}
              </div>
              <div className="txnpg-modal-sub">
                {editMode ? "Edit an existing transaction" : "Record a new transaction"}
              </div>
            </div>
            <button className="txnpg-modal-close" onClick={handleCloseModal} aria-label="Close">
              {React.cloneElement(ICONS.x, { size: 16 })}
            </button>
          </div>

          <div className="txnpg-modal-body">
            <div className="txnpg-form">
              {lockedHint && (
                <div className="txnpg-inline-warn">
                  <span style={{ fontSize: 14 }}>🔒</span>
                  <div>
                    <strong>This account is locked.</strong>
                    <br />
                    {lockedHint}
                  </div>
                </div>
              )}

              <div className="txnpg-form-row-2">
                <div className="txnpg-field-block">
                  <div className="txnpg-field-head">
                    <span className="txnpg-field-name">Date<span className="txnpg-req">*</span></span>
                    {formErrors.date && <span className="txnpg-field-err">{formErrors.date}</span>}
                  </div>
                  <div className="input">
                    <span className="input-pre">{React.cloneElement(ICONS.calendar, { size: 14 })}</span>
                    <input
                      type="date"
                      value={transactionForm.date}
                      onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="txnpg-field-block">
                  <div className="txnpg-field-head">
                    <span className="txnpg-field-name">Category<span className="txnpg-req">*</span></span>
                    {formErrors.category && <span className="txnpg-field-err">{formErrors.category}</span>}
                  </div>
                  <Autocomplete
                    size="small"
                    options={filteredCategories.slice().sort((a, b) => a.name.localeCompare(b.name))}
                    getOptionLabel={(opt) => opt.name || ""}
                    value={filteredCategories.find((c) => c._id === transactionForm.category) || null}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    onChange={(_, newVal) =>
                      setTransactionForm({ ...transactionForm, category: newVal?._id || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder={`Select ${modalType?.toLowerCase()} category…`} variant="outlined" />
                    )}
                    ListboxProps={{ style: { maxHeight: 240 } }}
                  />
                </div>
              </div>

              {modalType === "Transfer" ? (
                <div className="txnpg-form-row-2">
                  <div className="txnpg-field-block">
                    <div className="txnpg-field-head">
                      <span className="txnpg-field-name">From account<span className="txnpg-req">*</span></span>
                      {formErrors.fromAccount && <span className="txnpg-field-err">{formErrors.fromAccount}</span>}
                    </div>
                    <Autocomplete
                      size="small"
                      options={accounts
                        .filter((a) => a.isActive)
                        .slice()
                        .sort((a, b) => a.accountName.localeCompare(b.accountName))}
                      getOptionLabel={(opt) => opt.accountName || ""}
                      value={accounts.find((a) => a._id === transactionForm.fromAccount) || null}
                      isOptionEqualToValue={(o, v) => o._id === v._id}
                      onChange={(_, newVal) =>
                        setTransactionForm({ ...transactionForm, fromAccount: newVal?._id || "" })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select source account…" variant="outlined" />
                      )}
                      ListboxProps={{ style: { maxHeight: 240 } }}
                    />
                  </div>
                  <div className="txnpg-field-block">
                    <div className="txnpg-field-head">
                      <span className="txnpg-field-name">To account<span className="txnpg-req">*</span></span>
                      {formErrors.toAccount && <span className="txnpg-field-err">{formErrors.toAccount}</span>}
                    </div>
                    <Autocomplete
                      size="small"
                      options={accounts
                        .filter((a) => a.isActive)
                        .slice()
                        .sort((a, b) => a.accountName.localeCompare(b.accountName))}
                      getOptionLabel={(opt) => opt.accountName || ""}
                      value={accounts.find((a) => a._id === transactionForm.toAccount) || null}
                      isOptionEqualToValue={(o, v) => o._id === v._id}
                      onChange={(_, newVal) =>
                        setTransactionForm({ ...transactionForm, toAccount: newVal?._id || "" })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select destination account…" variant="outlined" />
                      )}
                      ListboxProps={{ style: { maxHeight: 240 } }}
                    />
                  </div>
                </div>
              ) : (
                <div className="txnpg-field-block">
                  <div className="txnpg-field-head">
                    <span className="txnpg-field-name">Account<span className="txnpg-req">*</span></span>
                    {formErrors.account && <span className="txnpg-field-err">{formErrors.account}</span>}
                  </div>
                  <Autocomplete
                    size="small"
                    options={accounts
                      .filter((a) => a.isActive)
                      .slice()
                      .sort((a, b) => a.accountName.localeCompare(b.accountName))}
                    getOptionLabel={(opt) => opt.accountName || ""}
                    value={accounts.find((a) => a._id === transactionForm.account) || null}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    onChange={(_, newVal) =>
                      setTransactionForm({ ...transactionForm, account: newVal?._id || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select an account…" variant="outlined" />
                    )}
                    ListboxProps={{ style: { maxHeight: 240 } }}
                  />
                </div>
              )}

              <div className="txnpg-field-block">
                <div className="txnpg-field-head">
                  <span className="txnpg-field-name">Amount<span className="txnpg-req">*</span></span>
                  {formErrors.amount && <span className="txnpg-field-err">{formErrors.amount}</span>}
                </div>
                <div className="input input-amount">
                  <span className="input-pre input-currency">{currencySymbol}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>

              <div className="txnpg-field-block">
                <div className="txnpg-field-head">
                  <span className="txnpg-field-name">Description</span>
                  <span className="txnpg-field-hint">Optional</span>
                </div>
                <textarea
                  className="ta"
                  rows={3}
                  placeholder={
                    modalType === "Receipt"
                      ? "e.g. Saturday market till takings"
                      : modalType === "Payment"
                      ? "e.g. Stationery — supplies"
                      : "e.g. Weekly sweep to deposit"
                  }
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                />
              </div>

              <div className="txnpg-field-block">
                <div className="txnpg-field-head">
                  <span className="txnpg-field-name">Attachment</span>
                  <span className="txnpg-field-hint">Optional — receipt or supporting file</span>
                </div>
                {attachmentFile ? (
                  <div>
                    <span className="txnpg-attach-pill">
                      {React.cloneElement(ICONS.paperclip, { size: 14 })}
                      <span>{attachmentFile.name}</span>
                      <button
                        type="button"
                        className="x-btn"
                        onClick={() => setAttachmentFile(null)}
                        aria-label="Remove attachment"
                      >
                        {React.cloneElement(ICONS.x, { size: 12 })}
                      </button>
                    </span>
                  </div>
                ) : (
                  <label className="txnpg-upload">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setAttachmentFile(f);
                      }}
                    />
                    <span className="txnpg-up-ic">
                      {React.cloneElement(ICONS.upload, { size: 14 })}
                    </span>
                    <div>
                      <div className="txnpg-up-line1">Upload receipt or attachment</div>
                      <div className="txnpg-up-line2">PNG, JPG or PDF · browse or take a photo</div>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="txnpg-modal-foot">
            <button className="btn btn-ghost" onClick={handleCloseModal} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editMode ? "Save changes" : `Save ${modalType?.toLowerCase()}`}
            </button>
          </div>
        </Dialog>

        {/* ─── View Transaction modal ─── */}
        <Dialog
          open={viewDialog.open}
          onClose={handleCloseView}
          fullWidth
          maxWidth={viewDialog.transaction?.attachment ? "md" : "sm"}
        >
          {viewDialog.transaction && (() => {
            const tx = viewDialog.transaction;
            const t = typeLower(tx.type);
            return (
              <>
                <div className={`txnpg-modal-head txnpg-modal-head-${t}`}>
                  <div className="txnpg-modal-head-l">
                    <div className="txnpg-modal-title">Transaction</div>
                    <div className="txnpg-modal-sub">
                      Reference · {tx._id ? tx._id.toUpperCase() : ""}
                    </div>
                  </div>
                  <button className="txnpg-modal-close" onClick={handleCloseView} aria-label="Close">
                    {React.cloneElement(ICONS.x, { size: 16 })}
                  </button>
                </div>

                <div className="txnpg-modal-body">
                  <div className={`txnpg-view-grid ${tx.attachment ? "with-attach" : ""}`}>
                    <div>
                      <div className="txnpg-view-hero">
                        <span className={`tchip tchip-${t}`}>{tx.type}</span>
                        <div className={`txnpg-view-amount amount-${t}`}>
                          {t === "receipt" ? "+ " : t === "payment" ? "− " : ""}
                          {formatMoney(Number(tx.amount), currency)}
                        </div>
                        <div className="txnpg-view-date">{fmtDateLong(tx.date)}</div>
                      </div>

                      <dl className="txnpg-view-dl">
                        {tx.type === "Transfer" ? (
                          <>
                            <dt>From account</dt>
                            <dd>{tx.fromAccount?.accountName || "—"}</dd>
                            <dt>To account</dt>
                            <dd>{tx.toAccount?.accountName || "—"}</dd>
                          </>
                        ) : (
                          <>
                            <dt>Account</dt>
                            <dd>{tx.account?.accountName || "—"}</dd>
                          </>
                        )}
                        <dt>Category</dt>
                        <dd>{tx.category?.name || "—"}</dd>
                        <dt>Description</dt>
                        <dd className="txnpg-view-desc">
                          {tx.description ? tx.description : <span className="txnpg-muted">No description</span>}
                        </dd>
                        {(() => {
                          // Only render "Entered by" when the server has populated
                          // createdBy with a user object. A raw ObjectId string
                          // (when populate is missed somewhere) is silently hidden
                          // rather than displayed.
                          const cb = tx.createdBy;
                          const enteredByName =
                            cb && typeof cb === "object"
                              ? cb.displayName || cb.username || null
                              : null;
                          if (!enteredByName && !tx.createdAt) return null;
                          return (
                            <>
                              <dt>Entered by</dt>
                              <dd>
                                {enteredByName || <span className="txnpg-muted">Unknown</span>}
                                {tx.createdAt && (
                                  <>
                                    {" · "}
                                    <span className="txnpg-muted">{fmtDateTime(tx.createdAt)}</span>
                                  </>
                                )}
                              </dd>
                            </>
                          );
                        })()}
                      </dl>
                    </div>

                    {tx.attachment && (
                      <aside className="txnpg-view-attach">
                        <div className="txnpg-va-head">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            {React.cloneElement(ICONS.paperclip, { size: 13 })}
                            <strong> Attachment</strong>
                          </span>
                          {signedAttachmentUrl && (
                            <a
                              className="btn btn-ghost btn-xs"
                              href={signedAttachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              {React.cloneElement(ICONS.download, { size: 12 })}
                              <span>Download</span>
                            </a>
                          )}
                        </div>
                        <div className="txnpg-va-pane">
                          {attachmentLoading ? (
                            <div className="txnpg-va-loading">
                              <div className="txnpg-spinner-sm" />
                              <div>Loading preview…</div>
                            </div>
                          ) : signedAttachmentUrl ? (
                            <img
                              className="txnpg-va-img"
                              src={signedAttachmentUrl}
                              alt="Attachment preview"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="txnpg-va-empty">Preview unavailable.</div>
                          )}
                        </div>
                      </aside>
                    )}
                  </div>
                </div>

                <div className="txnpg-modal-foot">
                  <button className="btn btn-ghost" onClick={handleCloseView}>Close</button>
                  <button className="btn btn-ghost" onClick={() => setConfirmDel(tx)}>
                    {React.cloneElement(ICONS.trash, { size: 13 })}
                    <span>Delete</span>
                  </button>
                  <button className="btn btn-primary" onClick={() => handleEdit(tx)}>
                    {React.cloneElement(ICONS.edit, { size: 13 })}
                    <span>Edit</span>
                  </button>
                </div>
              </>
            );
          })()}
        </Dialog>

        {/* ─── Delete confirm dialog ─── */}
        <Dialog
          open={!!confirmDel}
          onClose={() => setConfirmDel(null)}
          PaperProps={{ style: { borderRadius: 12 } }}
        >
          <div className="txnpg-confirm">
            <div className="txnpg-confirm-title">Delete transaction?</div>
            <div className="txnpg-confirm-text">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </div>
            <div className="txnpg-confirm-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={() => confirmDel && handleDelete(confirmDel._id)}
              >
                Delete
              </button>
            </div>
          </div>
        </Dialog>

        {/* ─── Snackbar / Toast ─── */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4500}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="standard"
            sx={{
              minWidth: 320,
              borderRadius: 2,
              boxShadow: "0 1px 0 rgba(0,0,0,.04), 0 6px 18px -8px rgba(15,23,42,.18), 0 2px 6px rgba(15,23,42,.06)",
              borderLeft: snackbar.severity === "success"
                ? "3px solid #2E7D32"
                : snackbar.severity === "warning"
                ? "3px solid #FFC107"
                : "3px solid #C62828",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
}

export default TransactionPage;
